const allowedTypes = new Set(['category', 'type']);
const norm = value => String(value || '').trim().toLowerCase();
const contextKeys = ['preview_theme_id', 'vehicle_selection_id', 'vehicle_id', 'vehicle_bucket_id', 'vehicle_make', 'vehicle_model', 'vehicle_year', 'vehicle_year_key', 'vehicle_make_key', 'vehicle_model_key', 'show_universal', 'show_universal_products', 'filter.p.m.custom.vehicle_make', 'filter.p.m.custom.vehicle_model', 'filter.p.m.custom.vehicle_year'];

export function eligibleNodes(manifest) {
  if (manifest?.schemaVersion !== 'ha-nav-manifest.v1' || !manifest.nodes || !manifest.integrity?.checksum) throw Error('INVALID_COMMERCE_GRAPH');
  return Object.values(manifest.nodes).filter(node => allowedTypes.has(node.semanticType)
    && node.kind === 'collection' && node.browse?.owned === true && node.browse.kind === 'product'
    && !node.visibility?.retired && !['hidden', 'held', 'retired'].includes(node.visibility?.state)
    && !node.browse.legacy && /^\/collections\/[a-z0-9-]+$/.test(node.url)
    && node.handle === node.url.split('/').at(-1));
}

export function validatePage(data, context, previous, page) {
  if (data?.schemaVersion !== 'hoonies-brand-range-page.v1' || data.handle !== context.handle
    || data.collectionId !== context.id || data.themeId !== context.themeId) throw Error('RANGE_RESPONSE_CONTEXT');
  if (!Number.isInteger(data.total) || data.total < 0 || data.total >= 25000
    || !Number.isInteger(data.pages) || data.pages !== Math.max(1, Math.ceil(data.total / 250))
    || data.page !== page || !Array.isArray(data.products)) throw Error('RANGE_RESPONSE_PAGINATION');
  if (previous && (data.total !== previous.total || data.pages !== previous.pages)) throw Error('RANGE_RESPONSE_DRIFT');
  if (data.products.length !== Math.min(250, data.total - (page - 1) * 250)) throw Error('RANGE_PAGE_INCOMPLETE');
  for (const row of data.products) {
    if (!/^\d+$/.test(row.id) || !row.handle || typeof row.vendor !== 'string' || !Array.isArray(row.collections)
      || row.collections.length !== row.collectionCount || row.collections.some(h => !/^[a-z0-9-]+$/.test(h))) throw Error('RANGE_PRODUCT_INCOMPLETE');
    if (!row.collections.includes(context.handle)) throw Error('RANGE_PRODUCT_OUTSIDE_BRAND');
  }
  return data;
}

export function buildRanges(manifest, products, vendor, priority = []) {
  const ids = new Set();
  for (const row of products) {
    if (ids.has(row.id)) throw Error('RANGE_DUPLICATE_PRODUCT');
    if (vendor && norm(row.vendor) !== norm(vendor)) throw Error('RANGE_MIXED_VENDOR_REQUIRES_COLLECTION_FILTER');
    ids.add(row.id);
  }
  const nodes = eligibleNodes(manifest), known = new Map(nodes.map(n => [n.handle, n]));
  const memberships = new Map();
  for (const product of products) for (const handle of new Set(product.collections)) {
    if (!known.has(handle)) continue;
    if (!memberships.has(handle)) memberships.set(handle, new Set());
    memberships.get(handle).add(product.id);
  }
  const rank = h => priority.includes(h) ? priority.indexOf(h) : Number.MAX_SAFE_INTEGER;
  const ranges = nodes.filter(node => memberships.has(node.handle)).map(node => ({
    handle: node.handle, label: node.shortLabel || node.label, url: node.url,
    semanticType: node.semanticType, parent: node.hierarchy?.parentId?.replace(/^collection:/, ''),
    order: node.hierarchy?.order || 0, productIds: [...memberships.get(node.handle)].sort(),
    ancestors: (node.hierarchy?.ancestorIds || []).map(id => id.replace(/^collection:/, '')),
    department: (node.hierarchy?.ancestorIds || []).map(id => manifest.nodes[id] || manifest.nodes[id.replace(/^collection:/, '')]).find(n => n?.semanticType === 'department')?.handle || '',
    category: [...(node.hierarchy?.ancestorIds || []), node.id].map(id => manifest.nodes[id] || manifest.nodes[id.replace(/^collection:/, '')]).find(n => n?.hierarchy?.depth === 1)?.handle || ''
  }));
  ranges.sort((a,b) => rank(a.handle) - rank(b.handle) || a.order - b.order || a.label.localeCompare(b.label, 'en'));
  return {schemaVersion:'hoonies-brand-ranges.v1', revision:manifest.integrity.checksum, vendor, total:ids.size, ranges};
}

export function buildRangeHierarchy(manifest, ranges, priority = []) {
  const eligible = new Set(eligibleNodes(manifest).map(node => node.handle));
  const nodes = new Map(Object.values(manifest.nodes).map(node => [node.handle, node]));
  const branches = new Map(), roots = [];
  const handleOf = id => id?.replace(/^collection:/, '');
  for (const range of ranges) {
    if (!eligible.has(range.handle)) throw Error('RANGE_HIERARCHY_INELIGIBLE');
    const path = [], seen = new Set();
    let node = nodes.get(range.handle);
    while (node) {
      if (seen.has(node.handle)) throw Error('RANGE_HIERARCHY_CYCLE');
      seen.add(node.handle); path.unshift(node);
      const parentHandle = handleOf(node.hierarchy?.parentId);
      if (!parentHandle) break;
      const parent = nodes.get(parentHandle);
      if (!parent || !parent.hierarchy?.childIds?.includes(node.id)) throw Error('RANGE_HIERARCHY_PARENT');
      node = parent;
    }
    if (path[0]?.semanticType !== 'department') throw Error('RANGE_HIERARCHY_DEPARTMENT');
    let parentBranch;
    for (const current of path) {
      let branch = branches.get(current.handle);
      if (!branch) {
        branch = {handle:current.handle, label:current.shortLabel || current.label, node:current, range:null, children:[]};
        branches.set(current.handle, branch);
        (parentBranch ? parentBranch.children : roots).push(branch);
      }
      parentBranch = branch;
    }
    if (parentBranch.range) throw Error('RANGE_HIERARCHY_DUPLICATE');
    parentBranch.range = range;
  }
  const rootOrder = manifest.browse?.roots || [];
  const rank = handle => priority.includes(handle) ? priority.indexOf(handle) : Number.MAX_SAFE_INTEGER;
  const sort = (siblings, parent) => {
    const order = parent?.node.hierarchy.childIds.map(handleOf) || rootOrder;
    siblings.sort((a,b) => {
      const declared = handle => order.includes(handle) ? order.indexOf(handle) : Number.MAX_SAFE_INTEGER;
      return (parent ? rank(a.handle) - rank(b.handle) : 0)
        || declared(a.handle) - declared(b.handle)
        || (a.node.hierarchy?.order ?? 1000) - (b.node.hierarchy?.order ?? 1000)
        || a.label.localeCompare(b.label, 'en');
    });
    for (const branch of siblings) sort(branch.children, branch);
  };
  sort(roots);
  return roots;
}

export function rangeLevel(hierarchy, handle = '') {
  if (!handle) return {path:[], sections:hierarchy.length > 1 ? [{branch:null, choices:hierarchy}] : hierarchy.map(department => ({branch:department, choices:department.children}))};
  const find = (branches, path) => {
    for (const branch of branches) {
      const next = [...path, branch];
      if (branch.handle === handle) return {path:next, sections:[{branch, choices:branch.children}]};
      const result = find(branch.children, next);
      if (result) return result;
    }
  };
  const level = find(hierarchy, []);
  if (!level || !level.sections[0].choices.length) throw Error('RANGE_LEVEL_UNAVAILABLE');
  return level;
}

export function rangeUrl(path, vendor, current) {
  const origin = new URL(current);
  const url = new URL(path, origin.origin);
  url.searchParams.set('catalogue_brand', vendor);
  url.searchParams.set('filter.p.vendor', vendor);
  for (const key of contextKeys) if (origin.searchParams.has(key)) url.searchParams.set(key, origin.searchParams.get(key));
  return url.pathname + url.search;
}

// A collection is the exact brand/sub-brand boundary, including OME's ARB subset.
// Existing catalogue hierarchy filters intersect this whole collection with the range.
export function collectionRangeUrl(range, collectionHandle, current) {
  if (!/^[a-z0-9-]+$/.test(collectionHandle)) throw Error('INVALID_BRAND_COLLECTION');
  const origin = new URL(current), url = new URL('/collections/' + collectionHandle, origin.origin);
  url.searchParams.set('view', 'all-products');
  if (range.department) url.searchParams.set('product_area', range.department);
  if (range.semanticType !== 'department') {
    if (range.category) url.searchParams.set('product_category', range.category);
    if (range.handle !== range.category) url.searchParams.set('product_type', range.handle);
  }
  for (const key of contextKeys) if (origin.searchParams.has(key)) url.searchParams.set(key, origin.searchParams.get(key));
  return url.pathname + url.search;
}

export function validateTile(data, handle, themeId) {
  if (data?.schemaVersion !== 'hoonies-brand-range-tile.v1' || data.handle !== handle
    || data.themeId !== themeId || !/^\d+$/.test(data.collectionId)
    || typeof data.html !== 'string' || !data.html.includes('data-brand-tile-count')) throw Error('RANGE_TILE_CONTEXT');
  return data;
}

async function refresh(root) {
  const context = {handle:root.dataset.brandCollection, id:root.dataset.brandCollectionId, themeId:root.dataset.themeId};
  const status = root.querySelector('[data-brand-range-status]');
  const groups = root.querySelector('[data-brand-range-groups]');
  const labels = JSON.parse(root.querySelector('[data-brand-range-labels]').textContent);
  const priority = JSON.parse(root.querySelector('[data-brand-range-priority]').textContent) || [];
  const vendor = root.dataset.brandVendor;
  const collectionScoped = root.dataset.brandRangeScope === 'collection';
  const destination = range => collectionScoped ? collectionRangeUrl(range, context.handle, location.href) : rangeUrl(range.url, vendor, location.href);
  let currentSnapshot, currentHierarchy, showLevel;
  let knMedia;
  let activeHandle = '';
  let requestedHandle = '';
  let levelRequest = 0;
  const tileRequests = new Map();
  const loadTile = async range => {
    if (range.tile) return validateTile(range.tile, range.handle, context.themeId);
    if (!tileRequests.has(range.handle)) {
      const url = new URL(range.url, location.origin);
      url.searchParams.set('view', 'brand-range-tile');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      tileRequests.set(range.handle, (async () => {
        const response = await fetch(url, {credentials:'same-origin', cache:'no-store', signal:AbortSignal.timeout(30000)});
        if (!response.ok) throw Error('RANGE_TILE_HTTP_' + response.status);
        return validateTile(await response.json(), range.handle, context.themeId);
      })().catch(error => {tileRequests.delete(range.handle); throw error;}));
    }
    range.tile = await tileRequests.get(range.handle);
    return range.tile;
  };
  const key = 'hoonies-brand-ranges:collection-progressive-v2:' + context.themeId + ':' + context.handle + ':' + (collectionScoped ? 'collection' : 'vendor') + (context.handle === 'k-n' && context.id === '716105843030' ? ':kn-native-media-v1' : '');
  const render = async (snapshot, manifest, cached = false) => {
    const hierarchy = buildRangeHierarchy(manifest, snapshot.ranges, priority);
    const departments = new Map((snapshot.departments || []).map(range => [range.handle, range]));
    if (hierarchy.length > 1 && hierarchy.some(branch => !departments.has(branch.handle))) throw Error('RANGE_DEPARTMENT_TILES_INCOMPLETE');
    const templates = new Map();
    const templateFor = range => {
      if (!templates.has(range.handle)) {
        validateTile(range.tile, range.handle, context.themeId);
        const template = document.createElement('template'); template.innerHTML = range.tile.html;
        const link = template.content.querySelector('a.range-tile');
        if (!link || !link.querySelector('h3') || !link.querySelector('[data-brand-tile-count]')
          || template.content.querySelector('script, iframe, object')) throw Error('RANGE_TILE_MARKUP');
        // Shared collection introductions can describe a different supplier.
        // The exact K&N brand context uses category names and native counts.
        if (context.handle === 'k-n' && context.id === '716105843030') {
          link.querySelector('.collection-category__tile-copy')?.remove();
          const entry = knMedia?.tiles[range.handle];
          if (!entry || !range.productIds.includes(entry.productId)) throw Error('RANGE_KN_IMAGE_MEMBERSHIP');
          const url = new URL(entry.image.url);
          if (url.hostname !== 'cdn.shopify.com' || !url.pathname.startsWith('/s/files/1/0952/1849/9926/files/')) throw Error('RANGE_KN_IMAGE_SOURCE');
          const image = document.createElement('img');
          url.searchParams.set('width', '720'); image.src = url.href;
          image.srcset = [360, 540, 720].map(width => {const candidate = new URL(entry.image.url); candidate.searchParams.set('width', width); return candidate.href + ' ' + width + 'w';}).join(', ');
          image.sizes = '(max-width: 749px) calc(100vw - 64px), (min-width: 1200px) 25vw, 33vw';
          image.alt = range.label; image.width = entry.image.width; image.height = entry.image.height;
          image.loading = 'lazy'; image.decoding = 'async';
          link.querySelector('.collection-category__tile-media').replaceChildren(image);
          link.dataset.rangeImageProductId = entry.productId;
        }
        templates.set(range.handle, link);
      }
      return templates.get(range.handle);
    };
    const countLabel = ids => ids.length + ' ' + (ids.length === 1 ? labels.one : labels.many);
    const productIds = branch => [...new Set([...(branch.range?.productIds || []), ...branch.children.flatMap(productIds)])].sort();
    const rangeOf = branch => {
      const range = branch.range || departments.get(branch.handle);
      if (range && branch.children.length) range.productIds = [...new Set([...range.productIds, ...productIds(branch)])].sort();
      return range;
    };
    const add = (branch, grid) => {
      const range = rangeOf(branch);
      const ids = range?.productIds.length ? range.productIds : productIds(branch);
      let tile;
      if (range) tile = templateFor(range).cloneNode(true);
      else {
        tile = document.createElement('a'); tile.className = 'range-tile';
        const media = document.createElement('span'); media.className = 'range-tile__img collection-category__tile-media';
        const placeholder = document.createElement('span'); placeholder.className = 'collection-category__tile-placeholder';
        placeholder.textContent = branch.label; media.append(placeholder);
        const body = document.createElement('span'); body.className = 'range-tile__body collection-category__tile-content';
        const title = document.createElement('h3'); body.append(title);
        const count = document.createElement('span'); count.className = 'go'; count.dataset.brandTileCount = ''; body.append(count);
        tile.append(media, body);
      }
      tile.querySelector('h3').textContent = branch.label;
      tile.querySelector('[data-brand-tile-count]').textContent = countLabel(ids);
      if (branch.children.length) {
        const button = document.createElement('button'); button.type = 'button'; button.className = tile.className + ' brand-range-choice';
        button.setAttribute('aria-label', branch.label + ', ' + countLabel(ids));
        button.setAttribute('aria-controls', groups.id);
        for (const block of [...tile.querySelectorAll('div, h3, p')].reverse()) {
          const span = document.createElement('span');
          for (const attribute of block.attributes) span.setAttribute(attribute.name, attribute.value);
          if (block.tagName === 'H3') span.classList.add('brand-range-title');
          span.append(...block.childNodes); block.replaceWith(span);
        }
        button.append(...tile.childNodes); tile = button;
        const arrow = document.createElement('span'); arrow.className = 'brand-range-arrow'; arrow.textContent = '›'; arrow.setAttribute('aria-hidden', 'true');
        tile.querySelector('[data-brand-tile-count]').append(arrow);
        tile.addEventListener('click', () => {openLevel(branch.handle, true).catch(() => {});});
      } else tile.href = destination(range);
      tile.dataset.rangeHandle = branch.handle;
      tile.dataset.productIds = JSON.stringify(ids);
      const item = document.createElement('li'); item.className = 'collection-category__tile'; item.append(tile); grid.append(item);
    };
    const drawLevel = (handle = '', focus = false, returnHandle = '') => {
      const level = rangeLevel(hierarchy, handle);
      const fragment = document.createDocumentFragment();
      const path = level.path.filter(branch => hierarchy.length > 1 || branch.node.semanticType !== 'department');
      if (path.length) {
        const nav = document.createElement('nav'); nav.className = 'brand-range-navigation'; nav.setAttribute('aria-label', labels.categories);
        const list = document.createElement('ol'); list.className = 'brand-range-breadcrumbs';
        for (const [index, crumb] of [{handle:'', label:labels.categories}, ...path].entries()) {
          const item = document.createElement('li');
          if (index === path.length) {
            const current = document.createElement('span'); current.setAttribute('aria-current', 'true'); current.textContent = crumb.label; item.append(current);
          } else {
            const back = document.createElement('button'); back.type = 'button'; back.textContent = crumb.label;
            back.dataset.rangeBack = crumb.handle;
            back.addEventListener('click', () => {openLevel(crumb.handle, true, path[index]?.handle || '').catch(() => {});});
            item.append(back);
          }
          list.append(item);
        }
        nav.append(list); fragment.append(nav);
      }
      for (const {branch, choices} of level.sections) {
        if (!choices.length) continue;
        const section = document.createElement('section'); section.className = 'brand-range-department'; section.dataset.rangeParent = branch?.handle || 'departments';
        if (branch) {
          const header = document.createElement('div'); header.className = 'brand-range-level-head';
          const heading = document.createElement('h3'); heading.className = 'brand-range-heading'; heading.textContent = branch.label; heading.tabIndex = -1; header.append(heading);
          const range = rangeOf(branch);
          if (handle && range?.productIds.length) {
            const shop = document.createElement('a'); shop.className = 'brand-range-shop'; shop.href = destination(range); shop.dataset.rangeHandle = branch.handle;
            shop.textContent = labels.shop + ' (' + countLabel(range.productIds) + ') →'; header.append(shop);
          }
          section.append(header);
        }
        const grid = document.createElement('ul'); grid.className = 'range-grid brand-range-tiles';
        grid.setAttribute('aria-label', branch?.label || labels.categories); grid.dataset.rangeParentGrid = branch?.handle || 'departments';
        for (const child of choices) add(child, grid);
        section.append(grid); fragment.append(section);
      }
      groups.replaceChildren(fragment);
      activeHandle = handle;
      root.dataset.rangeLevel = handle || 'categories';
      if (focus) {
        const returned = returnHandle && [...groups.querySelectorAll('.range-tile')].find(tile => tile.dataset.rangeHandle === returnHandle);
        (returned || groups.querySelector('.brand-range-heading') || groups.querySelector('.range-tile'))?.focus();
      }
    };
    const openLevel = async (handle = '', focus = false, returnHandle = '') => {
      const request = ++levelRequest;
      requestedHandle = handle;
      root.setAttribute('aria-busy', 'true');
      status.textContent = labels.loading;
      status.hidden = false;
      try {
        const choices = rangeLevel(hierarchy, handle).sections.flatMap(section => section.choices);
        const needed = choices.map(rangeOf).filter(Boolean);
        for (let offset = 0; offset < needed.length; offset += 4) await Promise.all(needed.slice(offset, offset + 4).map(loadTile));
        if (request !== levelRequest) return false;
        drawLevel(handle, focus, returnHandle);
        currentHierarchy = hierarchy;
        currentSnapshot = snapshot;
        showLevel = openLevel;
        root.querySelector('[data-brand-range-fallback]')?.setAttribute('hidden', '');
        root.dataset.rangeRevision = snapshot.revision;
        root.dataset.rangeTotal = snapshot.total;
        root.dataset.rangeState = cached ? 'cached' : 'complete';
        root.dataset.rangeLayout = 'progressive-v2';
        status.textContent = snapshot.ranges.length ? '' : labels.empty;
        status.hidden = !!snapshot.ranges.length;
        try { sessionStorage.setItem(key, JSON.stringify(snapshot)); } catch {}
        return true;
      } catch (error) {
        if (request === levelRequest) {
          requestedHandle = activeHandle;
          status.textContent = labels.error;
          status.hidden = false;
          root.dispatchEvent(new CustomEvent('hoonies:brand-ranges-error', {bubbles:true, detail:{handle:context.handle, error:error.message}}));
        }
        throw error;
      } finally {
        if (request === levelRequest) root.removeAttribute('aria-busy');
      }
    };
    const focusWasInside = groups.contains(document.activeElement);
    const focusHandle = document.activeElement?.dataset.rangeHandle || '';
    let targetHandle = requestedHandle;
    try { rangeLevel(hierarchy, targetHandle); } catch { targetHandle = ''; }
    return openLevel(targetHandle, focusWasInside, focusHandle);
  };
  const readPage = async page => {
    const url = new URL('/collections/' + context.handle, location.origin);
    url.searchParams.set('view', 'brand-range-data'); url.searchParams.set('page', page);
    url.searchParams.set('sort_by', 'created-ascending');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const response = await fetch(url, {credentials:'same-origin', cache:'no-store', signal:AbortSignal.timeout(30000)});
    if (!response.ok) throw Error('RANGE_HTTP_' + response.status);
    return response.json();
  };
  const all = root.querySelector('[data-brand-range-all]');
  groups.id ||= root.id + '-levels';
  groups.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !activeHandle || !currentHierarchy) return;
    const path = rangeLevel(currentHierarchy, activeHandle).path.filter(branch => currentHierarchy.length > 1 || branch.node.semanticType !== 'department');
    event.preventDefault(); showLevel(path.at(-2)?.handle || '', true, activeHandle).catch(() => {});
  });
  const preserve = () => {
    const url = new URL('/collections/' + context.handle, location.origin);
    url.searchParams.set('view', 'all-products');
    for (const name of contextKeys) {
      const value = new URL(location.href).searchParams.get(name);
      if (value !== null) url.searchParams.set(name, value);
    }
    all.href = url.pathname + url.search;
    for (const link of groups.querySelectorAll('a[data-range-handle]')) {
      const range = [...(currentSnapshot?.ranges || []), ...(currentSnapshot?.departments || [])].find(r => r.handle === link.dataset.rangeHandle);
      if (range) link.href = destination(range);
    }
  };
  preserve();
  window.addEventListener('popstate', preserve);
  window.addEventListener('pageshow', preserve);
  try {
    const firstPage = readPage(1);
    firstPage.catch(() => {});
    const response = await fetch(root.dataset.manifestUrl, {credentials:'same-origin', signal:AbortSignal.timeout(30000)});
    if (!response.ok) throw Error('RANGE_GRAPH_HTTP_' + response.status);
    const manifest = await response.json(); eligibleNodes(manifest);
    if (context.handle === 'k-n' && context.id === '716105843030') {
      const mediaUrl = new URL('kn-brand-range-media.v1.json', import.meta.url);
      mediaUrl.searchParams.set('v', manifest.integrity.checksum);
      const mediaResponse = await fetch(mediaUrl, {credentials:'same-origin', cache:'no-store', signal:AbortSignal.timeout(30000)});
      if (!mediaResponse.ok) throw Error('RANGE_KN_MEDIA_HTTP_' + mediaResponse.status);
      knMedia = await mediaResponse.json();
      if (knMedia.schemaVersion !== 'hoonies-kn-brand-range-media.v1' || knMedia.collectionId !== context.id || knMedia.handle !== context.handle || knMedia.manifestRevision !== manifest.integrity.checksum) throw Error('RANGE_KN_MEDIA_CONTEXT');
    }
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      if (saved?.revision === manifest.integrity.checksum && saved.vendor === vendor && saved.collectionId === context.id) await render(saved, manifest, true);
    } catch {}
    const first = validatePage(await firstPage, context, null, 1);
    const rows = [...first.products];
    for (let page = 2; page <= first.pages; page++) rows.push(...validatePage(await readPage(page), context, first, page).products);
    if (first.pages > 1) {
      const check = validatePage(await readPage(1), context, first, 1);
      if (JSON.stringify(check.products) !== JSON.stringify(first.products)) throw Error('RANGE_SNAPSHOT_DRIFT');
    }
    const snapshot = {...buildRanges(manifest, rows, collectionScoped ? '' : vendor, priority), vendor, collectionId:context.id, fetchedAt:new Date().toISOString()};
    if (snapshot.total !== first.total) throw Error('RANGE_TOTAL_MISMATCH');
    const hierarchy = buildRangeHierarchy(manifest, snapshot.ranges, priority);
    snapshot.departments = hierarchy.length > 1 ? hierarchy.map(branch => ({handle:branch.handle,label:branch.label,url:branch.node.url,semanticType:'department',department:branch.handle,category:'',productIds:rows.filter(row => row.collections.includes(branch.handle)).map(row => row.id).sort()})) : [];
    await render(snapshot, manifest);
    try { sessionStorage.setItem(key, JSON.stringify(snapshot)); } catch {}
    root.dispatchEvent(new CustomEvent('hoonies:brand-ranges-ready', {bubbles:true, detail:snapshot}));
  } catch (error) {
    root.dataset.rangeState = 'error';
    if (!groups.children.length) {
      root.querySelector('[data-brand-range-fallback]')?.removeAttribute('hidden');
      status.hidden = false; status.textContent = labels.error;
    }
    console.error('Hoonies brand range refresh failed', context.handle, error.message);
    root.dispatchEvent(new CustomEvent('hoonies:brand-ranges-error', {bubbles:true, detail:{handle:context.handle, error:error.message}}));
    if (window.Shopify?.designMode) {status.hidden = false; status.textContent = labels.error + ' ' + error.message;}
  }
}

if (typeof document !== 'undefined') for (const root of document.querySelectorAll('[data-brand-range-discovery]')) refresh(root);
